import {createSelector, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "./Store";

export type TStep = {
    title: string,
    step: number;
    label: string;
    description: string;
    questions: string[];
    showErrors: boolean;
    answers: { index: number; answer: boolean | null }[]
}

export type CustomerDataState = {
    stepQuestions: TStep[];
}

const initialState: CustomerDataState = {
    stepQuestions: [
        {
            step: 0,
            label: "شناخت مشتری",
            title: 'زیربنا',
            answers: [],
            showErrors: false,
            description: 'پیش از هرارتباطی باید مشتری و پروژه‌ش رو بیشتر از یک نام بشناسید. چه طوری؟',
            questions: [
                `- در CRM و یا آرشیو تماس‌های قبلی‌م شماره‌شو می‌زنم و پروژه‌ها و مکالمات قبلی که داشتیم رو می‌خونم تا ببینم چه صحبت‌هایی کردیم تا نیازهاشو بهتر بشناسم.`,
                `-\tشماره‌شو ذخیره می‌کنم تا عکس‌های پروفایلش در تلگرام و پیام‌رسان‌های دیگه رو ببینم و بتونم سن و سال، روحیات و سلایقش رو بهتر بفهمم و ارتباط همدلانه‌تری بسازم.`,
                `-\tپروژه‌ش رو در بانک اطلاعات پرشین سازه باز می‌کنم، مشخصات کامل، مکان پروژه، عکس‌ها و سرعت پیشرفت پروژه رو درمیارم و این طوری به تحلیل و شناخت بهتری از پروژه‌ش و وضعیت حال حاضرش می‌رسم تا ارتباط درست‌تری بسازم.`,
            ],
        },
        {
            step: 1,
            title: 'اسکلت‌بندی',
            answers: [],
            label: "راهکارها، بازار و رقبا",
            showErrors: false,
            description: 'در نگاه اول شاید بگید معلومه که محصول/خدمات خودمو می‌شناسم. اما بیاید با یه مفهومی آشنا بشیم که در این زمینه بهتون کمک می‌کنه: FAB. مفهوم FAB مخفف سه واژه است:',
            questions: [
                `-\tویژگی‌ها / مشخصات (Features): من ویژگی‌های محصول/خدمت و راهکارهایی که ارائه می‌کنم رو کامل و جامع می‌دونم و روشون تسلط دارم.`,
                `-\tمزیت‌ها (Advantages): من علاوه بر ویژگی‌ها، مزیت‌هایی که نسبت به محصولات دیگه و رقبا دارم رو هم می‌دونم و می‌تونم اونها رو به خوبی و مسلط ارائه و نشون بدم.`,
                `-\tسودمندی‌ها (Benefits): این روزها ویژگی‌ها و مزیت‌های همه‌ی محصولات/خدمات مشابه، تقریبا همسان شده و فرق زیادی بین رقبا نیست. مهم‌تر اینه که بتونیم نشون بدیم همکاری با ما چه سودی برای سازنده‌ها داره. یعنی نشون بدیم که خرید از ما براشون سودمندتره، چه مالی و چه غیرمالی. من این کار رو بلدم.`,
                `فروش همیشه از وضعیت بازار و رقبا متاثره. ما توی کارمون متناسب با شرایط بازار خودمونو به‌روز می‌کنیم و راهکارهای مختلفی، مثل شرایط پرداخت منعطف، تخفیف‌ها، خدمات پس از فروش و تنوع در محصولات/خدماتمون ارائه می‌دیم.`,
            ],
        },
        {
            step: 2,
            title: 'دیوارچینی و نازک‌کاری',
            label: "ارتباط‌گیری و فرآیند فروش",
            answers: [],
            showErrors: false,
            description: `
                حالا که مرحله‌ی شناخت تکمیل شد، باید خودمونو برای ارتباط‌گیری آماده کنیم. فراموش نکنید که هدف
                فقط فروش نیست، بلکه ساختن یک تجربه‌ی خوب و حل مشکل سازنده‌س. سازندگان به کسانی اعتماد می‌کنند که
                نیازها و خواسته‌هاشون رو درک کرده باشن. با این حساب به نظرتون چه کارهایی لازمه؟`,
            questions: [
                `-\tما پیش از هر ارتباط، مساله‌ها و چالش‌هایی که ممکنه مشتری مطرح کنه رو پیش‌بینی می‌کنیم و براشون راهکارها و پاسخ‌های مناسب آماده می‌کنیم تا مثل یک مشاور همراه مشتری باشیم.`,
                `-\tما محتواهای تکمیلی مثل کاتالوگ زیبا و مناسب، اسناد فنی لازم، ویدیوهای رضایت مشتریان، نمونه‌کارها، وب‌سایت به‌روز و … رو داریم و متناسب با پیشبرد گفتگو با مشتری براش ارسال می‌کنیم.`,
                `-\tما از ابزارهای کمکی ساده‌ی مارکتینگی مثل پنل پیامک پرشین سازه برای معرفی و ارتباط با سازنده‌ها استفاده می‌کنیم.`,
                `-\tما اصول تماس سرد و برقراری ارتباط موثر رو بلدیم و از این روش‌ها به درستی و بهینه استفاده می‌کنیم.`,
            ],
        },
        {
            step: 3,
            title: 'پایان‌کار',
            label: "پس از فروش",
            answers: [],
            showErrors: false,
            description: `
                مبارک باشه، شما یک فروش خوب داشتید. اما صبر کنید، همون‌طور که گفتیم فروش یک فرآینده و با بستن
                قرارداد تموم نمی‌شه. پس از نهایی شدن فروش شما هم‌چنان باید حواستون به پروژه و خود مشتری باشه.`,
            questions: [
                `-\tما بعد از تحویل کار هم، با مشتری در تماسیم تا از رضایتش مطمئن بشیم و اگه مشکلی در روند بوده براش برطرف کنیم. این‌طوری می‌تونیم باهاش یه رابطه‌ی بلندمدت و فروش‌های بعدی رو کلید بزنیم.`,
                `-\tما تلاش می‌کنیم در بازه‌های زمانی مختلف و به بهانه‌های خوب ارتباط‌مون رو با مشتری نگه داریم و رابطه‌ای حرفه‌ای و بلندمدت بسازیم.`,
            ],
        }
    ],
}

const customerDataSlice = createSlice({
    name: 'customerData',
    initialState,
    reducers: {
        setInitialState(state) {
            state = initialState;
        },
        answer: (state: CustomerDataState, action: PayloadAction<{
            step: number,
            index: number,
            answer: boolean
        }>) => {
            const {step, index, answer} = action.payload;
            const ans = state.stepQuestions.find(sq => sq.step === step)?.answers.find(a => a.index === index);

            state.stepQuestions = state.stepQuestions.map(sq => sq.step !== step ? sq : ({
                ...sq,
                answers: ans ? sq.answers.map(a => a.index === index ? ({
                    ...a,
                    answer: answer
                }) : a) : [...sq.answers, {index, answer}]
            }))
        },

        setShowErrors: (state: CustomerDataState, action: PayloadAction<{ step: number }>) => {
            state.stepQuestions = state.stepQuestions.map(s => s.step === action.payload.step ? ({
                ...s,
                showErrors: true
            }) : s)
        },
    }
})

const selectStepOptions = (state: RootState) => state.customerDataSlice.stepQuestions;

export const selectActiveStep = createSelector(
    [selectStepOptions, (_: RootState, step: number) => step],
    (stepQuestions, step): TStep | undefined => stepQuestions.find(s => s.step === step))

export const {
    setShowErrors,
    answer,
    setInitialState
} = customerDataSlice.actions;

export default customerDataSlice.reducer;




